import { useTranslation } from "react-i18next";
import useMockEnrolmentState from "../State/useMockEnrolmentState";
import useEnrolmentState from "../State/useEnrolmentState";
import useUpdateEffectiveDate from "../Hooks/useUpdateEffectiveDate";
import useUpdateMaturityDate from "../Hooks/useUpdateMaturityDate";
import useValidateMaturityDate from "../Hooks/useValidateMaturityDate";
import useUpdateDepositCodeBasedOnMatAndEffectiveDates from "../Hooks/useUpdateDepositCodeBasedOnMatAndEffectiveDates";
import useClearJointSubscriberForSingleEnrolment from "../Hooks/useClearJointSubscriberForSingleEnrolment";
import useSetCLBBasedOnBirthdate from "../Hooks/useSetCLBBasedOnBirthdate";
import useApplyNewAgentToEnrolment from "../Hooks/useApplyNewAgentToEnrolment";
import useIsGroupPlanAllowed from "../Hooks/useIsGroupPlanAllowed";
import useEnforceCLBOnlyAllowed from "../Hooks/useEnforceCLBOnlyAllowed";
import useUpdateQuoteDefaults from "../Hooks/useUpdateQuoteDefaults";
import useUpdateUnitsBasedOnPlanType from "../Hooks/useUpdateUnitsBasedOnPlanType";
import useUpdateUnitsBasedOnDeposit from "../Hooks/useUpdateUnitsBasedOnDeposit";
import useResetPlan2PlanType from "../Hooks/useResetPlan2PlanType";
import useSetMonthlyAvailableSavingsForRespContributions from "../Hooks/useSetMonthlyAvailableSavingsForRespContributions";
import useSyncChildEnrolments from "../Hooks/useSyncChildEnrolments";

import BasicInputDataCollection from "./BasicInputDataCollection";
import FirstPlanDataCollection from "./FirstPlanDataCollection";
import SubscribersDataCollection from "./SubscribersDataCollection";
import BeneficiaryDataCollection from "./BeneficiaryDataCollection";
import PDFViewer from "../PDFGeneration/PDFViewer";
import GroupQuote from "../Quotes/GroupQuote";
import QuotePDFViewer from "../Quotes/QuotePDFViewer";
import QuoteAllModes from "../Quotes/QuoteAllModes";
import XMLViewer from "../XMLGeneration/XMLViewer";
import NavigationController from "../NavigationController/NavigationController";
import JSONViewer from "../JSONGeneration/JSONViewer";
import GuardianDataCollection from "./GuardianDataCollection";
import PaymentDataCollection from "./PaymentDataCollection";
import BCTESGDataCollection from "./BCTESGDataCollection";
import TransferDataCollection from "./TransferDataCollection";
import { useAuthenticationStatus } from "../Authentication/AuthenticationStatusProvider";
import useValidateMaximumCarryForwardGrantRoom from "../Hooks/useValidateMaximumCarryForwardGrantRoom";
import useUpdateInsuranceOptions from "../Hooks/useUpdateInsuranceOptions";
import useLoadEnrolmentData from "../Hooks/useLoadEnrolmentData";
import useAssignDefaultAgentValues from "../Hooks/useAssignDefaultAgentValues";
import useUpdateDepositBasedOnUnits from "../Hooks/useUpdateDepositBasedOnUnits";
import KnowYourClientDataCollection from "./KnowYourClientDataCollection";
import DRChecklist from "./DRChecklist";
import EnrollmentPackageViewer from "../PDFGeneration/EnrollmentPackageViewer";
import { useEffect, useRef } from "react";
import { saveEnrollment } from "../Utils/APIUtils";
import { useHeaderContext } from "../Header/HeaderContextProvider";
import EnrollmentProgressStepper from "../EnrollmentProgressStepper/EnrollmentProgressStepper";
import { useParams } from "react-router-dom";
import SecondPlanDataCollection from "./SecondPlanDataCollection";
import useAllowOnlyOnePrimaryCaregiver from "../Hooks/useAllowOnlyOnePrimaryCaregiver";
import { Tooltip } from "react-tooltip";
import moment from "moment";
import { useState } from "react";
import { PLAN_TYPE_IDS } from "../Utils/PlanUtils";
import FamilyEnrolments from "../FamilyEnrolments/FamilyEnrolments";
import useUpdateDependant1BasedOnBene from "../Hooks/useUpdateDependant1BasedOnBene";
import useSetBCProvincialGrantBasedOnBirthdate from "../Hooks/useSetBCProvincialGrantBasedOnBirthdate";
import { Helmet } from "react-helmet";
import SuitabilityRationaleDataCollection from "./SuitabilityRationaleDataCollection";

const EnrolmentNavigationController = ({ lngs }) => {
    const params = useParams();
    const saveTimeoutRef = useRef(null);
    const { authenticationStatus } = useAuthenticationStatus();
    const { setCenteredHeaderContent } = useHeaderContext();
    const [changesSaved, setChangesSaved] = useState(false);

    const { t, i18n } = useTranslation();

    //   STATE
    //useEnrolmentState for has default values for the state
    let enrolmentState = useEnrolmentState();
    //enrolmentState = useMockEnrolmentState();

    //   HOOKS
    // These are hooks that are used to update the state of the enrolment based on changes
    // Much of the business logic will be within hooks the fire when certain values are updated
    // to the subscription, subscriber, jointSubscriber, beneficiary, etc...

    //Assign the default agent values to the enrolment, if the user is authenticated
    useAssignDefaultAgentValues(enrolmentState);
    //If an ID was provided in the URL, we will load the enrolment data for that ID
    useLoadEnrolmentData(enrolmentState);
    //Set effective date based on beneficiary birthDate
    useUpdateEffectiveDate(enrolmentState);
    //Set maturity date based on beneficiary birthDate and effective date
    useUpdateMaturityDate(enrolmentState);
    //Validate an updated maturity date based on business rules
    useValidateMaturityDate(enrolmentState);
    //Update the deposit code based on maturity date and effective date
    useUpdateDepositCodeBasedOnMatAndEffectiveDates(enrolmentState);
    //Set the CLB based on the beneficiary birthdate (2004 rule kind of not needed anymore)
    useSetCLBBasedOnBirthdate(enrolmentState);
    //Clear first and lastname of joint subscriber if we are switching to Single enrolment
    useClearJointSubscriberForSingleEnrolment(enrolmentState);
    //When an agent is selected, we apply agent values to different parts of the enrolment
    useApplyNewAgentToEnrolment(enrolmentState);
    //If the group option plan is selected and it is not allowed, unselect it.
    useIsGroupPlanAllowed(enrolmentState);
    //CLB Only is only allowed for SI plan
    useEnforceCLBOnlyAllowed(enrolmentState);
    //Validate the maximum carry forward grant room
    useValidateMaximumCarryForwardGrantRoom(enrolmentState);
    //Determine which insurance options should be turned off based on the options selected
    useUpdateInsuranceOptions(enrolmentState);
    useUpdateQuoteDefaults(enrolmentState);
    useUpdateUnitsBasedOnPlanType(enrolmentState);
    useUpdateUnitsBasedOnDeposit(enrolmentState);
    useUpdateDepositBasedOnUnits(enrolmentState);
    useAllowOnlyOnePrimaryCaregiver(enrolmentState);
    useResetPlan2PlanType(enrolmentState);
    useSyncChildEnrolments(enrolmentState);
    useUpdateDependant1BasedOnBene(enrolmentState);
    useSetMonthlyAvailableSavingsForRespContributions(enrolmentState);
    useSetBCProvincialGrantBasedOnBirthdate(enrolmentState);

    //Use destructuring to remove the enrollment sub-object since we do not want it in the dependency array
    // because it gets updated during the save process
    const { enrollment, ...rest } = enrolmentState;
    //We filter out all of the set methods as well
    const filteredObject = Object.fromEntries(
        Object.entries(rest).filter(([key, value]) => !key.startsWith("set")),
    );

    const saveNow = (state) => {
        saveEnrollment(state)
            .then((response) => {
                setChangesSaved(true);
            })
            .catch((error) => {
                alert(
                    "There was an error saving the enrolment state. Please try again.",
                );
                // TODO: Improve this to show a better error message, possibly at the top of the screen
            });
    };

    useEffect(() => {
        setChangesSaved(false);
        // Reset the save timer
        if (!enrolmentState.enrollment._id) {
            return;
        }
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }
        saveTimeoutRef.current = setTimeout(() => {
            saveNow(enrolmentState);
        }, 2000); // Save after 2 seconds of inactivity

        // Cleanup on component unmount
        return () => {
            if (saveTimeoutRef.current) {
                clearTimeout(saveTimeoutRef.current);
            }
        };
    }, Object.values(filteredObject)); //This creates a dependency array with everything in the enrollmentState except for the enrollment sub-object, and none of the set methods

    useEffect(
        () => {
            setCenteredHeaderContent(
                <div>
                    {enrolmentState.enrollment.state !== "pre-docusign" && (
                        <div className={"enrolment-locked-icon"}>
                            <span
                                className="material-symbols-outlined"
                                data-tooltip-content={t(
                                    "This enrolment has been submitted to DocuSign and can no longer be edited.",
                                )}
                                data-tooltip-id={"locked-tooltip"}
                                data-tooltip-place="right"
                            >
                                lock
                            </span>
                        </div>
                    )}
                    {(enrolmentState.beneficiary.firstName ||
                        enrolmentState.beneficiary.lastName) && (
                            <div>
                                {t("Beneficiary")}:{" "}
                                {enrolmentState.beneficiary.firstName}{" "}
                                {enrolmentState.beneficiary.lastName}
                            </div>
                        )}
                    {(enrolmentState.subscriber.firstName ||
                        enrolmentState.subscriber.lastName) && (
                            <div>
                                {t("Subscriber")}:{" "}
                                {enrolmentState.subscriber.firstName}{" "}
                                {enrolmentState.subscriber.lastName}
                            </div>
                        )}
                    {(enrolmentState.jointSubscriber.firstName ||
                        enrolmentState.jointSubscriber.lastName) &&
                        enrolmentState.common.subscriber2Include && (
                            <div>
                                {t("Joint Subscriber")}:{" "}
                                {enrolmentState.jointSubscriber.firstName}{" "}
                                {enrolmentState.jointSubscriber.lastName}
                            </div>
                        )}
                    {enrolmentState.enrollment.state === "pre-docusign" &&
                        changesSaved &&
                        enrolmentState.enrollment.lastSaveAttemptSuccessful && (
                            <div className={"recently-saved-icon"}>
                                <span
                                    className="material-symbols-outlined"
                                    data-tooltip-content={t("Changes saved!")}
                                    data-tooltip-id={"locked-tooltip"}
                                    data-tooltip-place="right"
                                >
                                    check_circle
                                </span>
                            </div>
                        )}
                    {enrolmentState.enrollment.state === "pre-docusign" &&
                        changesSaved &&
                        !enrolmentState.enrollment
                            .lastSaveAttemptSuccessful && (
                            <div className={"recently-saved-icon"}>
                                <span
                                    className="material-symbols-outlined"
                                    data-tooltip-content={t(
                                        "Last save attempt failed.",
                                    )}
                                    data-tooltip-id={"locked-tooltip"}
                                    data-tooltip-place="right"
                                >
                                    warning
                                </span>
                            </div>
                        )}
                    {enrolmentState.enrollment.state === "pre-docusign" &&
                        !changesSaved && (
                            <div className={"save-button"}>
                                <span
                                    className="clickable"
                                    onClick={() => {
                                        saveNow(enrolmentState);
                                    }}
                                >
                                    {enrolmentState.enrollment
                                        .lastSaveAttemptSuccessful ===
                                        false && (
                                            <span
                                                className="material-symbols-outlined"
                                                data-tooltip-content={t(
                                                    "Last save attempt failed.",
                                                )}
                                                data-tooltip-id={"locked-tooltip"}
                                                data-tooltip-place="right"
                                            >
                                                warning
                                            </span>
                                        )}
                                    <span className="material-symbols-outlined">
                                        save
                                    </span>
                                    Save
                                </span>
                            </div>
                        )}

                    <Tooltip id={"locked-tooltip"} className={""} />
                </div>,
            );

            console.log("setting header");
        },
        Object.values(filteredObject).concat([changesSaved]),
    ); //enrolmentState.beneficiary, enrolmentState.subscriber, enrolmentState.jointSubscriber, enrolmentState.enrollment.state, enrolmentState.enrollment.lastSuccessfulSaveTime, enrolmentState.enrollment.lastSaveAttemptSuccessful, changesSaved]);

    //   NAVIGATION ITEMS
    //The key is the name of the navigation item as displayed in the navbar,
    // the value is the component to display
    // the 'visible' prop is used to determine when the component should be displayed
    const navigationItems = {
        [t("Basic")]: (
            <BasicInputDataCollection state={enrolmentState} visible={true} />
        ),
        [t("Know your Client")]: (
            <KnowYourClientDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    !enrolmentState.application.masterApplicationId
                } // TODO unsure what this is based on
            />
        ),
        [t("First Plan")]: (
            <FirstPlanDataCollection
                state={enrolmentState}
                visible={enrolmentState.beneficiary.birthdate}
            />
        ),
        [t("Second Plan")]: (
            <SecondPlanDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    enrolmentState.plan.planTypeID !== -1
                }
            />
        ),
        [t("Subscribers")]: (
            <SubscribersDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    !enrolmentState.application.masterApplicationId
                }
            />
        ),
        [t("Beneficiary")]: (
            <BeneficiaryDataCollection
                state={enrolmentState}
                visible={enrolmentState.beneficiary.birthdate}
            />
        ),
        [t("Guardian")]: (
            <GuardianDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    !enrolmentState.subscriber.primaryCaregiver &&
                    !enrolmentState.jointSubscriber.primaryCaregiver
                }
            />
        ),
        [t("BCTESG")]: (
            <BCTESGDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    enrolmentState.agent.province === "BC"
                } // TODO determine if this is correct way to get the rep's province
            // visible={enrolmentState.beneficiary.birthdate} // WARNING: This is just a temporary fix to make the BCTESG form visible for testing
            />
        ),
        [t("Payment")]: (
            <PaymentDataCollection
                state={enrolmentState}
                visible={enrolmentState.beneficiary.birthdate}
            />
        ),
        [t("Transfer")]: (
            <TransferDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    enrolmentState.plan2.planTypeID === PLAN_TYPE_IDS.TRANSFER
                }
            />
        ),

        [t("Suitability Rationale")]: (
            <SuitabilityRationaleDataCollection
                state={enrolmentState}
                visible={
                    enrolmentState.beneficiary.birthdate &&
                    enrolmentState.plan.planTypeID !== -1
                }
            />
        ),
        [t("DR Checklist")]: (
            <DRChecklist state={enrolmentState} visible={true} />
        ),

        [t("Review Enrolment Forms")]: (
            <EnrollmentPackageViewer
                state={enrolmentState}
                visible={enrolmentState.plan.planTypeID !== -1}
            />
        ),

        [t("Family Enrolments")]: (
            <FamilyEnrolments state={enrolmentState} visible={true} />
        ),

        /*
        //This needs to be moved to a button in the First and Second Plan areas
        'Generate Quotes': <QuoteAllModes
            state={enrolmentState}
            showAllSimultaneously={false}
            visible={enrolmentState.plan.planTypeID !== -1}
        />,

        //This is just temporary to have a spot to see the XML
        'Review XML': <XMLViewer
            data={enrolmentState}
            visible={true}
        />,
         */

        //This is just temporary to have a spot to see the XML
        "Raw Data (for testing)": (
            <JSONViewer
                data={enrolmentState}
                visible={process.env.NODE_ENV === "development"}
            />
        ),

        /*
        //This is just temporary to have a spot to change the language
        'Language':
            <div visible={true}>
                {Object.keys(lngs).map((lng) => (
                    <button key={lng} style={{fontWeight: i18n.resolvedLanguage === lng ? 'bold' : 'normal'}}
                            type="submit" onClick={() => i18n.changeLanguage(lng)}>
                        {lngs[lng].nativeName}
                    </button>
                ))}
            </div>
            */
    };

    if (authenticationStatus.isAuthenticated) {
        let title = "Enrolment";
        if (
            enrolmentState.beneficiary.firstName ||
            enrolmentState.beneficiary.lastName
        ) {
            title = `${title} - ${enrolmentState.beneficiary.firstName} ${enrolmentState.beneficiary.lastName}`;
        }
        return (
            <div className="enrolment-navigation-controller">
                <Helmet>
                    <title>{title}</title>
                </Helmet>
                <NavigationController
                    navigationItems={navigationItems}
                    initialItem={params.subElement}
                    enrolmentId={enrolmentState.enrollment._id}
                />
            </div>
        );
    }
};

export default EnrolmentNavigationController;
